import deepFreeze from 'deep-freeze';

import { devtoolsApi } from '#/dev/devtools';
import { MainConfig } from '#/service/config/config.types';
import { defaultConfig } from '#/service/config/defaultConfig';
import { RawConfig } from '#/service/config/raw.types';
import { initializeConfig } from '#/service/config/transform';
import { falsyToNull } from '#/service/utils';

import { geti18NextConfig } from './i18nextConfig';
import { getMetaAttributes } from './meta';

// If any of these variables are modified, remember to change them in publicPath.js as well
const ONE_META_NAME = 'one-data';

enum MetaKey {
  Config = 'data-one-config',
  Translations = 'data-one-translations',
  Locale = 'data-one-locale',
  StaticFilePrefix = 'data-one-static-file-prefix',
  Version = 'data-one-static-bundle-version',
  RouterVersion = 'data-one-router-version',
  AppId = 'data-one-application-id',
  VariationHash = 'data-one-variation-hash',
}

// Describes the structure of the global one object
type OneObjectInterface = Readonly<{
  env: {};
  config: MainConfig;
  appId?: string;
  variationHash?: string;
}>;

const defaultOne: OneObjectInterface = {
  env: {},
  config: defaultConfig,
};

type MetaAttrTupleType = [
  RawConfig,
  any,
  string,
  string,
  string,
  string,
  string,
  string,
];

function getDataFromMetaAttributes(): OneObjectInterface | null {
  const [
    config,
    translations,
    locale,
    staticFilePrefix,
    version,
    routerVersion,
    appId,
    variationHash,
  ] = getMetaAttributes<MetaAttrTupleType>(ONE_META_NAME, [
    {
      name: MetaKey.Config,
      parse: true,
    },
    {
      name: MetaKey.Translations,
      parse: true,
    },
    MetaKey.Locale,
    MetaKey.StaticFilePrefix,
    MetaKey.Version,
    MetaKey.RouterVersion,
    MetaKey.AppId,
    MetaKey.VariationHash,
  ]);

  if (!config) {
    return null;
  }

  console.debug('Initial locale', locale);
  const i18nConfig = geti18NextConfig(
    falsyToNull(locale) ?? 'en',
    translations?.translations
  );

  const processedConfig = initializeConfig(
    config,
    i18nConfig,
    staticFilePrefix,
    version,
    routerVersion
  );

  if (process.env.goal === 'local') {
    devtoolsApi.setConfig(processedConfig);
  }

  return {
    env: {},
    config: processedConfig,
    appId,
    variationHash,
  };
}

export const oneObject: OneObjectInterface = deepFreeze(
  getDataFromMetaAttributes() || defaultOne
) as OneObjectInterface;
